import React from 'react'

import { isPredictablePassword } from '@bat/shared'
import { useTranslation } from '@platform/i18n/hooks/useTranslation'

import { useTheme } from '@/shared/hooks/useTheme'

interface PasswordRequirementsProps {
  password: string
}

export const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({ password }) => {
  const { t } = useTranslation('auth')
  const { isDark } = useTheme()

  if (!password) return null

  // Mirrors the ENFORCED server policy (@bat/shared platform/validation):
  // 8+ chars, ≥1 lowercase [a-z], ≥1 digit — `passwordValidation` — MÁS las dos
  // reglas de `password-policy.ts` que el registro y el cambio de contraseña
  // aplican igualmente (`passwordValidator.validatePassword`, auth.service.ts).
  // Uppercase/special chars siguen SIN listarse: no son requisitos del
  // servidor, y enseñarlos como tales engañaba en la otra dirección.
  //
  // La cuarta línea no estaba, y su ausencia se medía: 'Sa2Probe123!' pintaba
  // los tres requisitos en verde, el indicador anunciaba «cumple todos los
  // requisitos» y el registro devolvía 400 citando una regla que el usuario
  // nunca había visto. Se lee del MISMO predicado que juzga en el servidor,
  // así que no puede volver a separarse.
  const requirements = [
    { met: password.length >= 8, label: t('passwordRequirements.minLength') },
    { met: /[a-z]/.test(password), label: t('passwordRequirements.lowercase') },
    { met: /\d/.test(password), label: t('passwordRequirements.number') },
    { met: !isPredictablePassword(password), label: t('passwordRequirements.noPredictable') },
  ]

  const bgClass = isDark ? 'bg-crypto-border/10' : 'bg-gray-50'
  const titleClass = isDark ? 'text-crypto-text' : 'text-gray-700'
  const labelClass = isDark ? 'text-crypto-muted' : 'text-gray-600'

  return (
    <div className={`${bgClass} rounded-lg p-4 space-y-2 mt-2`}>
      <h4 className={`text-xs font-medium ${titleClass}`}>{t('passwordRequirements.title')}</h4>
      <ul className={`text-xs ${labelClass} space-y-1`}>
        {requirements.map((req) => (
          <li key={req.label} className="flex items-center space-x-2">
            <span className={req.met ? 'text-crypto-success' : 'text-crypto-muted'}>
              {req.met ? '\u2713' : '\u25CB'}
            </span>
            <span>{req.label}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
